import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clubsBookingApi } from '@/api/clubs-booking';
import { auth } from '@/store/authStore';
import ParentPortalShell from '@/components/parent/ParentPortalShell';
import { formatCurrencyFromCents } from '@/utils/currency';

type ParentBooking = Awaited<ReturnType<typeof clubsBookingApi.listParentBookings>>[number];
type BookingClubMeta = {
  logoUrl?: string;
  clubName?: string;
};

const formatSessionDateTime = (startDateTime?: string, endDateTime?: string) => {
  if (!startDateTime || !endDateTime) return 'Time unavailable';
  const start = new Date(startDateTime);
  const end = new Date(endDateTime);
  return `${start.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}, ${start.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  })} - ${end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const getBookingChildren = (booking: ParentBooking) => {
  const children = Array.isArray(booking.children)
    ? booking.children
        .map((child) => [String(child?.firstName || '').trim(), String(child?.lastName || '').trim()].filter(Boolean).join(' ').trim())
        .filter(Boolean)
    : [];
  return children.length > 0 ? children : ['Child'];
};

const getCartItemChildIndexes = (item: { childIndexes?: number[] }, childCount: number) => {
  const validIndexes = (item.childIndexes || [])
    .map((childIndex) => Number(childIndex))
    .filter((childIndex) => Number.isInteger(childIndex) && childIndex >= 0 && childIndex < childCount);
  return validIndexes.length ? Array.from(new Set(validIndexes)) : Array.from({ length: childCount }, (_, index) => index);
};

const getSessionChildren = (booking: ParentBooking) => {
  const childNames = getBookingChildren(booking);
  const childCount = Math.max(1, childNames.length || booking.quantity || 1);
  const cartItems = booking.cartItems?.length
    ? booking.cartItems
    : [{ sessionIds: booking.sessionIds || [], childIndexes: [] }];
  const childrenBySessionId = new Map<string, Set<string>>();

  cartItems.forEach((item) => {
    const childIndexes = getCartItemChildIndexes(item, childCount);
    (item.sessionIds || booking.sessionIds || []).forEach((sessionId) => {
      const sessionKey = String(sessionId);
      const children = childrenBySessionId.get(sessionKey) || new Set<string>();
      childIndexes.forEach((childIndex) => {
        children.add(childNames[childIndex] || `Child ${childIndex + 1}`);
      });
      childrenBySessionId.set(sessionKey, children);
    });
  });

  return childrenBySessionId;
};

const ParentBookingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState<ParentBooking[]>([]);
  const [clubMetaByEventId, setClubMetaByEventId] = useState<Record<string, BookingClubMeta>>({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const token = auth.getToken();
        if (!token) {
          setError('Your parent session has expired. Please log in again.');
          return;
        }
        clubsBookingApi.setAuthToken(token);
        const data = await clubsBookingApi.listParentBookings();
        setItems(data || []);

        const eventIds = Array.from(new Set((data || []).map((booking) => booking.event?.id).filter(Boolean))) as string[];
        const entries = await Promise.all(
          eventIds.map(async (eventId) => {
            try {
              const event = await clubsBookingApi.getPublicEvent(eventId);
              return [
                eventId,
                {
                  logoUrl: event?.clubProfile?.logoUrl,
                  clubName: event?.clubProfile?.clubName || '',
                },
              ] as const;
            } catch {
              return [eventId, { logoUrl: undefined, clubName: '' }] as const;
            }
          }),
        );
        setClubMetaByEventId(Object.fromEntries(entries));
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          auth.clearAuth();
          clubsBookingApi.clearAuthToken();
          window.location.assign('/parent/login');
          return;
        }
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not load bookings');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  return (
    <ParentPortalShell title="My bookings" subtitle="Simple list of what you booked and when">
      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        {loading && <p className="text-sm text-slate-500">Loading bookings...</p>}
        {error && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        {!loading && !error && (
          <div className="space-y-3">
            {items.map((booking) => {
              const eventSessions = booking.event?.sessions || [];
              const sessionChildren = getSessionChildren(booking);
              const selectedIds = Array.from(sessionChildren.keys());
              const selectedSessions = selectedIds
                .map((sessionId) => eventSessions.find((session) => session.id === sessionId))
                .filter(Boolean) as Array<{ id: string; startDateTime: string; endDateTime: string }>;
              const displaySessions = selectedSessions;
              const eventId = booking.event?.id || '';
              const clubMeta = eventId ? clubMetaByEventId[eventId] : undefined;
              const clubName = clubMeta?.clubName || booking.event?.title || 'Club';
              const clubInitials = clubName
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part.charAt(0).toUpperCase())
                .join('') || 'SM';

              return (
                <article key={booking._id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        {clubMeta?.logoUrl ? (
                          <img
                            src={clubMeta.logoUrl}
                            alt={clubName}
                            data-testid={`parent-booking-club-logo-${booking._id}`}
                            className="h-10 w-10 shrink-0 rounded-md object-contain"
                          />
                        ) : (
                          <div
                            data-testid={`parent-booking-club-logo-fallback-${booking._id}`}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#F3FBF7] text-xs font-semibold text-[#1F8F5F]"
                          >
                            {clubInitials}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="truncate text-lg font-semibold text-slate-900">{booking.event?.title || 'Activity'}</p>
                          <p className="truncate text-sm text-slate-600">{booking.event?.location || 'Location unavailable'}</p>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                      <p className="text-xs text-emerald-700">Booking total</p>
                      <p className="text-base font-semibold text-emerald-900">
                        {formatCurrencyFromCents(booking.totalPriceCents, booking.currency || 'GBP')}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-2 text-sm sm:grid-cols-4">
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="text-xs text-slate-500">Children</p>
                      <p className="font-medium text-slate-900">{getBookingChildren(booking).join(', ')}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="text-xs text-slate-500">Payment</p>
                      <p className="font-medium text-slate-900">{booking.paymentStatus === 'paid' ? 'Paid' : 'Pay later'}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="text-xs text-slate-500">Cost</p>
                      <p className="font-medium text-slate-900">
                        {formatCurrencyFromCents(booking.totalPriceCents, booking.currency || 'GBP')}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="text-xs text-slate-500">Discount</p>
                      <p className="font-medium text-slate-900">
                        -{formatCurrencyFromCents(booking.discountTotalCents || 0, booking.currency || 'GBP')}
                      </p>
                    </div>
                  </div>

                  {displaySessions.length > 0 && (
                    <div className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="text-xs font-medium text-slate-500">Booked sessions</p>
                      <ul className="mt-1 space-y-1 text-sm text-slate-700">
                        {displaySessions.map((session) => (
                          <li key={session.id}>
                            <span>{formatSessionDateTime(session.startDateTime, session.endDateTime)}</span>
                            <span className="block text-xs text-slate-500">
                              {Array.from(sessionChildren.get(session.id) || []).join(', ')}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!!booking.appliedDiscounts?.length && (
                    <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                      <p className="text-xs font-medium text-emerald-800">Applied discounts</p>
                      <ul className="mt-1 space-y-1 text-sm text-emerald-900">
                        {booking.appliedDiscounts.map((discount) => (
                          <li key={`${booking._id}-${discount.discountId}`}>
                            {discount.summary} (-{formatCurrencyFromCents(discount.amountCents, booking.currency || 'GBP')})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {booking.event?.id && (
                    <div className="mt-3">
                      <Link to={`/book/${booking.event.id}`} className="text-sm font-medium text-[#1F8F5F] hover:underline">
                        View activity page
                      </Link>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center">
            <p className="text-sm text-slate-600">No bookings yet.</p>
            <p className="mt-1 text-xs text-slate-500">When you book an activity, it will appear here.</p>
          </div>
        )}
      </section>
    </ParentPortalShell>
  );
};

export default ParentBookingsPage;

